import React from 'react';
import { Shield, Truck, Headphones, CheckCircle, Award, Zap, Star, Globe, Heart } from 'lucide-react';

export default function About() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-[#0a0f0d] via-[#0d1a14] to-[#0a0f0d] text-white pt-20 px-4 pb-20 font-[Rajdhani] relative overflow-hidden">
      {/* Premium background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#ffcc32]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#ffcc32]/3 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-[#ffcc32]/5 to-transparent rounded-full blur-3xl"></div>
      </div>

      <section className="max-w-6xl mx-auto relative z-10">
        {/* Premium header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#ffcc32]/10 border border-[#ffcc32]/30 rounded-full px-4 py-2 mb-6">
            <Star className="text-[#ffcc32]" size={16} />
            <p className="text-[#ffcc32] font-bold uppercase text-xs tracking-[0.3em]">Premium Quality</p>
            <Star className="text-[#ffcc32]" size={16} />
          </div>
          <h1 className="text-6xl md:text-8xl lg:text-9xl uppercase mb-4 leading-none" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.1em' }}>
            RRIDE
          </h1>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-[0.4em] text-[#ffcc32] mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            MOTO STUDIO
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#ffcc32] to-transparent mx-auto mb-8"></div>
        </div>
        
        {/* Premium introduction */}
        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 mb-16 shadow-2xl shadow-black/50">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-[#ffcc32]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Globe className="text-[#ffcc32]" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.1em' }}>Our Story</h3>
              <p className="text-zinc-300 text-lg leading-relaxed">
                RRide Moto Studio is a premium motorcycle accessories and customization brand dedicated to riders across India. We specialize in providing high-quality motorcycle accessories, riding gear, performance parts, and customization solutions to enhance your riding experience.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#ffcc32]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Heart className="text-[#ffcc32]" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.1em' }}>Our Commitment</h3>
              <p className="text-zinc-400 text-lg leading-relaxed">
                Operating under <span className="text-[#ffcc32] font-semibold">RRide Bike Garage and Studio</span>, we are committed to delivering genuine products with reliable service and customer satisfaction.
              </p>
            </div>
          </div>
        </div>

        {/* What We Offer - Premium Grid */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.15em' }}>
              What We Offer
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#ffcc32] to-transparent mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              'Motorcycle Accessories',
              'Riding Gear',
              'Performance Parts',
              'LED Lighting',
              'Genuine Spare Parts',
              'Touring Accessories',
              'Bike Customization',
              'Premium Riding Essentials'
            ].map((item, index) => (
              <div key={index} className="group bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:border-[#ffcc32]/50 hover:bg-[#ffcc32]/5 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl hover:shadow-[#ffcc32]/10">
                <div className="w-10 h-10 bg-[#ffcc32]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#ffcc32]/30 transition">
                  <Star className="text-[#ffcc32]" size={16} />
                </div>
                <p className="text-white font-semibold text-sm uppercase tracking-wider" style={{ fontFamily: 'Cinzel, serif' }}>{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mission & Vision - Premium Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <div className="group bg-gradient-to-br from-[#ffcc32]/15 via-[#ffcc32]/5 to-transparent border border-[#ffcc32]/30 rounded-2xl p-8 md:p-10 hover:border-[#ffcc32]/50 transition-all duration-300 hover:shadow-2xl hover:shadow-[#ffcc32]/10 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#ffcc32]/20 rounded-full flex items-center justify-center group-hover:bg-[#ffcc32]/30 transition">
                <Zap className="text-[#ffcc32]" size={32} />
              </div>
              <h3 className="text-3xl font-bold uppercase tracking-wider" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.1em' }}>Our Mission</h3>
            </div>
            <p className="text-zinc-300 text-lg leading-relaxed">
              To provide premium quality motorcycle accessories and riding products with reliable service and customer satisfaction, ensuring every rider gets the best products for their journey.
            </p>
          </div>
          <div className="group bg-gradient-to-br from-[#ffcc32]/15 via-[#ffcc32]/5 to-transparent border border-[#ffcc32]/30 rounded-2xl p-8 md:p-10 hover:border-[#ffcc32]/50 transition-all duration-300 hover:shadow-2xl hover:shadow-[#ffcc32]/10 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#ffcc32]/20 rounded-full flex items-center justify-center group-hover:bg-[#ffcc32]/30 transition">
                <Award className="text-[#ffcc32]" size={32} />
              </div>
              <h3 className="text-3xl font-bold uppercase tracking-wider" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.1em' }}>Our Vision</h3>
            </div>
            <p className="text-zinc-300 text-lg leading-relaxed">
              To become one of India's most trusted motorcycle accessories brands, known for quality, reliability, and exceptional customer service in the riding community.
            </p>
          </div>
        </div>

        {/* Why Choose Us - Premium Features */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.15em' }}>
              Why Choose RRide
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#ffcc32] to-transparent mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: CheckCircle, title: 'Genuine Products', desc: '100% authentic products sourced directly from manufacturers' },
              { icon: Shield, title: 'Quality Checked', desc: 'Every product is thoroughly inspected before shipping' },
              { icon: Truck, title: 'Fast Shipping', desc: 'Quick delivery across India with reliable courier partners' },
              { icon: Headphones, title: 'Dedicated Support', desc: 'Expert customer support for all your queries' },
              { icon: Award, title: 'Trusted by Riders', desc: 'Building trust with thousands of satisfied customers' },
              { icon: Zap, title: 'Secure Payments', desc: 'Safe and secure payment processing through Razorpay' }
            ].map((feature, index) => (
              <div key={index} className="group bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-[#ffcc32]/50 hover:bg-[#ffcc32]/5 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl hover:shadow-[#ffcc32]/10">
                <div className="w-16 h-16 bg-[#ffcc32]/20 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#ffcc32]/30 transition">
                  <feature.icon className="text-[#ffcc32]" size={32} />
                </div>
                <h3 className="text-xl font-bold uppercase mb-3 tracking-wider" style={{ fontFamily: 'Cinzel, serif', letterSpacing: '0.1em' }}>{feature.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
